import React, { useEffect, useState, useRef } from 'react';
import { useUserSettings } from '../stores/settingsRegistry';
import { useChatHistoryStore } from '../stores/chatHistoryStore';

interface ModelSelectProps {
    onModelChange: (modelId: string) => void;
    isModelVLMSupported: boolean;
}

export function ModelSelect({ onModelChange, isModelVLMSupported: _isModelVLMSupported }: ModelSelectProps) {
    // Subscribe to dropdownOptions directly to trigger re-render when async load completes
    // (helpers is a stable object reference that won't trigger re-renders on its own)
    const { helpers: settingsHelpers, dropdownOptions: _dropdownOptions } = useUserSettings();
    const inUseVLM = useChatHistoryStore((s) => s.inUseVLM);
    const hasImages = useChatHistoryStore((s) => s.hasImages);
    const selectedModel = useChatHistoryStore((s) => s.selectedModel);
    const setSelectedModel = useChatHistoryStore((s) => s.setSelectedModel);
    const [isOpen, setIsOpen] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Extract models and default model from settings with proper type guards
    const getModelOptions = () => {
        if (!settingsHelpers) return [];
        const config = settingsHelpers.getConfig('defaultModel');
        if (!config || config.controlType !== 'select' || !('options' in config)) {
            return [];
        }
        return config.options || [];
    };

    const models = getModelOptions();
    const defaultModelConfig = settingsHelpers?.getConfig('defaultModel');
    const defaultModel = defaultModelConfig ? settingsHelpers?.getValue('defaultModel') as string : '';

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Initialize only once to prevent infinite re-initialization loops
    useEffect(() => {
        if (!hasInitialized && defaultModel && models.length > 0) {
            const modelExists = models.some(m => m.value === defaultModel);
                            if (modelExists) {
                    setSelectedModel(defaultModel);
                    onModelChange(defaultModel);
                    setHasInitialized(true);
                } else if (models.length > 0) {
                // Fallback when stored default model is no longer available
                    setSelectedModel(models[0].value);
                    onModelChange(models[0].value);
                    setHasInitialized(true);
                }
            }
    }, [defaultModel, models, hasInitialized, onModelChange, setSelectedModel]);

    // Allow re-initialization when selectedModel is cleared (e.g., loading a chat with no model)
    useEffect(() => {
        if (hasInitialized && !selectedModel && models.length > 0) {
            setHasInitialized(false);
        }
    }, [selectedModel, hasInitialized, models]);

    const handleModelSelect = (modelId: string) => {
        if (!modelId) {
            setSelectedModel(modelId);
            onModelChange(modelId);
            setIsOpen(false);
            return;
        }

        const modelSupportsVLM = settingsHelpers.isVLM(modelId);

        if ((inUseVLM || hasImages) && !modelSupportsVLM) {
            return;
        }

        setSelectedModel(modelId);
        onModelChange(modelId);
        setIsOpen(false);
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const getDisplayText = () => {
        if (selectedModel) {
            // Find the model to show its label if available
            const modelOption = models.find(m => m.value === selectedModel);
            return modelOption?.label || selectedModel;
        }
        return "Select a model";
    };

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                className="dropdown-trigger flex items-center"
                style={{ WebkitAppRegion: 'no-drag' } as any}
                title="Choose an AI model"
                onClick={toggleDropdown}
                type="button"
                data-testid="model-select"
            >
                <span className="truncate" style={{minWidth: '150px', maxWidth: 'max-content'}}>{getDisplayText()}</span>
                <i className={`fas fa-chevron-down transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ml-2`} />
            </button>

            {isOpen && (
                <div className="dropdown-menu" style={{minWidth: '100%', width: 'max-content'}}>
                    <div className="dropdown-option" onClick={() => handleModelSelect('')}>
                        Select a model
                    </div>
                    {models.map((model) => {
                        const isVLM = settingsHelpers.isVLM(model.value);
                        const isDisabled = (inUseVLM || hasImages) && !isVLM;

                        return (
                            <div
                                key={model.value}
                                className={`dropdown-option ${
                                    selectedModel === model.value ? 'dropdown-option-selected' : ''
                            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            style={{overflowWrap: 'anywhere'}}
                            onClick={() => !isDisabled && handleModelSelect(model.value)}
                            title={isDisabled ? "This model doesn't support VLM" : model.label}
                        >
                            <div className="flex items-center">
                                <span className={isDisabled ? 'text-gray-500' : ''}>{model.label}</span>
                                {isVLM && (
                                    <span className={`ml-2 px-1.5 py-0.5 text-xs flex items-center rounded-md ${
                                        selectedModel === model.value
                                        ? 'text-white'
                                        : 'text-red-500'
                                    }`} title="VLM support">
                                        <i className="fas fa-image" />
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
                </div>
            )}
        </div>
    );
}
